import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, Timer, Target, TrendingUp } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';

const FocusTimer: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState<'work' | 'break' | 'longBreak'>('work');
  const [sessions, setSessions] = useState(0);
  const [totalFocusTime, setTotalFocusTime] = useState(0);

  const modes = {
    work: { duration: 25, label: 'Focus Time', color: 'bg-red-500' },
    break: { duration: 5, label: 'Short Break', color: 'bg-green-500' },
    longBreak: { duration: 15, label: 'Long Break', color: 'bg-blue-500' }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      handleTimerComplete();
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft]);

  const handleTimerComplete = () => {
    if (mode === 'work') {
      setSessions(sessions + 1);
      setTotalFocusTime(totalFocusTime + 25);
      
      // Auto-switch to break after work session
      if ((sessions + 1) % 4 === 0) {
        setMode('longBreak');
        setTimeLeft(15 * 60);
      } else {
        setMode('break');
        setTimeLeft(5 * 60);
      }
    } else {
      // Switch back to work after break
      setMode('work');
      setTimeLeft(25 * 60);
    }
    
    // Notification would go here
    new Notification(`${modes[mode].label} complete!`, {
      body: mode === 'work' ? 'Time for a break!' : 'Ready for another focus session?'
    });
  };

  const toggleTimer = () => {
    setIsActive(!isActive);
  };

  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(modes[mode].duration * 60);
  };

  const switchMode = (newMode: 'work' | 'break' | 'longBreak') => {
    setMode(newMode);
    setTimeLeft(modes[newMode].duration * 60);
    setIsActive(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    const totalTime = modes[mode].duration * 60;
    return ((totalTime - timeLeft) / totalTime) * 100;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto project-mgmt-page">
      <PageTracker pageTitle="Focus Timer" category="project-management" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Focus Timer</h1>
        <p className="text-gray-600 text-lg">
          Boost productivity with the Pomodoro Technique. Work in focused 25-minute intervals 
          followed by short breaks to maintain concentration and prevent burnout.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-8 mb-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {modes[mode].label}
          </h2>
          
          <div className="relative w-64 h-64 mx-auto mb-6">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - getProgress() / 100)}`}
                className={`transition-all duration-1000 ${
                  mode === 'work' ? 'text-red-500' : 
                  mode === 'break' ? 'text-green-500' : 'text-blue-500'
                }`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {formatTime(timeLeft)}
                </div>
                <div className="text-sm text-gray-500">
                  Session {sessions + 1}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4 mb-6">
            <button
              onClick={toggleTimer}
              className={`px-6 py-3 rounded-lg text-white font-medium flex items-center space-x-2 ${
                isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isActive ? <Pause size={20} /> : <Play size={20} />}
              <span>{isActive ? 'Pause' : 'Start'}</span>
            </button>
            
            <button
              onClick={resetTimer}
              className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium flex items-center space-x-2 hover:bg-gray-50"
            >
              <RotateCcw size={20} />
              <span>Reset</span>
            </button>
          </div>
          
          <div className="flex justify-center space-x-2">
            {Object.entries(modes).map(([modeKey, modeInfo]) => (
              <button
                key={modeKey}
                onClick={() => switchMode(modeKey as 'work' | 'break' | 'longBreak')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mode === modeKey
                    ? `${modeInfo.color} text-white`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {modeInfo.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100">Sessions Today</p>
              <p className="text-3xl font-bold">{sessions}</p>
            </div>
            <Target size={32} className="text-red-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Focus Time</p>
              <p className="text-3xl font-bold">{totalFocusTime}m</p>
            </div>
            <Timer size={32} className="text-green-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Current Streak</p>
              <p className="text-3xl font-bold">5</p>
            </div>
            <TrendingUp size={32} className="text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Best Streak</p>
              <p className="text-3xl font-bold">12</p>
            </div>
            <Target size={32} className="text-purple-200" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Progress</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Daily Goal: 8 sessions</span>
                <span className="font-medium">{sessions}/8</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((sessions / 8) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Focus Time Goal: 200 min</span>
                <span className="font-medium">{totalFocusTime}/200</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((totalFocusTime / 200) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-indigo-900 mb-4">Pomodoro Tips</h3>
          <ul className="text-sm text-indigo-800 space-y-2">
            <li>• Choose a specific task before starting</li>
            <li>• Remove all distractions (phone, notifications)</li>
            <li>• Take breaks seriously - step away from work</li>
            <li>• After 4 sessions, take a longer 15-30 min break</li>
            <li>• Track what you accomplish during each session</li>
            <li>• Adjust timer durations to fit your workflow</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FocusTimer;
